/**
 * Sentry noise filter — single source of truth for both the `beforeSend` hook
 * AND the `ignoreErrors` config across server, edge, and client init paths.
 *
 * `ignoreErrors` runs EARLIER in the SDK pipeline (before sampling, before
 * tagging) — fast path. `beforeSend` runs at the boundary (slower, has full
 * event context). Both need the same regex set so a noise pattern that's
 * partially-tagged before `beforeSend` runs doesn't slip through.
 *
 * Tightening rule: every entry is anchored (`^...$`) so it matches the exact
 * message — never substring. The v1 pattern `/aborted$/i` would have matched
 * "Request aborted unexpectedly" and "Transaction aborted" (both real errors).
 */
import type { ErrorEvent, EventHint } from '@sentry/nextjs';

/**
 * Canonical Sentry enable-gate — single source of truth for the `enabled` flag
 * across all three init paths (client / server / edge).
 *
 * Sentry initializes only when a DSN is configured AND we are NOT in local dev.
 * Initializing the SDK under `next dev` (Next 16 + Turbopack + Node 24) corrupts
 * the streaming response the framework relies on:
 *   TypeError: controller[kState].transformAlgorithm is not a function
 * Server-side that throw aborts the RSC stream; the dev client can't reconcile the
 * partial payload and issues a full-document reload, which re-runs init and throws
 * again — an infinite reload loop that hammers the server until it OOMs. Sentry in
 * local dev has no value anyway (errors surface in the terminal/console directly).
 *
 * `NODE_ENV` is 'development' only under `next dev`; every deployed Vercel env
 * (preview + production) builds and runs with 'production', so this keeps Sentry
 * fully active everywhere it matters. Set NEXT_PUBLIC_SENTRY_FORCE_DEV=true to opt
 * back in locally when specifically testing Sentry itself.
 */
export const isSentryEnabled: boolean =
  !!process.env.NEXT_PUBLIC_SENTRY_DSN &&
  (process.env.NODE_ENV !== 'development' || process.env.NEXT_PUBLIC_SENTRY_FORCE_DEV === 'true');

export const NOISE_PATTERNS: readonly RegExp[] = [
  /^Rate limit exceeded$/, // Existing — anchored.
  /^AbortError$/i, // Client/browser cancellation (XHR/fetch AbortController).
  /^aborted$/, // node:_http_server abortIncoming (client disconnect mid-request).
] as const;

/**
 * Framework / library noise that should be dropped at the SDK boundary BEFORE
 * any sampling or tagging. Single source for `ignoreErrors` across all three
 * init paths (server / edge / client).
 *
 * Includes:
 * - NOISE_PATTERNS (so they short-circuit even earlier than beforeSend).
 * - Framework-bug noise that has its own tracking (e.g. Next.js client manifest).
 */
export const IGNORE_ERRORS: readonly RegExp[] = [
  ...NOISE_PATTERNS,
  // Next.js dev-only framework bug — InvariantError "The client reference manifest
  // for '...' does not exist". Tracked upstream; ignore until fix lands.
  /client reference manifest for ".*" does not exist/,
  // Stack Auth library-internal assertion (#6268-G). @stackframe/stack pinned at 2.8.89
  // (2.8.104+ introduced session regressions — idle logouts via _useSession Suspense
  // changes; reverted 2026-06-03). Assertion still present in 2.8.89; no upstream fix.
  // The assertion fires when Stack's preflight check sees a freshly-minted JWT with
  // 0ms remaining validity (race between mint and the 20000ms expiry assertion).
  // Stack-side, not actionable from our code. ~639 ev/30d (NEXTJS-65 + NEXTJS-5A).
  // Anchored to the exact throw message (ends with " (${expiresInMillis}ms)") so it
  // cannot substring-match a wrapped re-throw. Remove when Stack releases a fix and
  // we upgrade; see docs/notes/6268-G-stack-auth-investigation.md. Tracked: #6446.
  /^Required access token expiry \d+ms is too long; access tokens are too short when they're generated \(\d+ms\)$/,
] as const;

export function beforeSend(event: ErrorEvent, _hint: EventHint): ErrorEvent | null {
  const message = event.exception?.values?.[0]?.value ?? '';
  for (const pattern of NOISE_PATTERNS) {
    if (pattern.test(message)) {
      return null;
    }
  }
  return event;
}
